// PO-GST-01 · P2-D2 · Management Approval GST comparison. Stored queue values only — no GST maths.
import {
  TOTAL_ROWS, totalDiff, rateLabel, shortFp, fmtTs, money, chargeText, fieldLabel,
} from "@/lib/poApprovalGst";
import { SUPPLIER_CLASS_FINDING, SUPPLIER_CLASS_GUIDANCE } from "@/lib/supplierGstClass";

const chg = "text-cyan-300 bg-cyan-500/10 font-semibold";
const same = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
const taxType = (c) => (!c?.tax_type ? "—" : c.tax_type === "intra" ? "Intra-state · CGST+SGST" : "Inter-state · IGST");

function StatusChip({ status, testid }) {
  const ok = status === "COMPUTED";
  return (
    <span data-testid={testid} className={`px-1.5 py-0.5 rounded-sm border text-[10px] font-mono uppercase ${ok ? "border-emerald-500/40 text-emerald-300" : "border-amber-500/40 text-amber-300"}`}>
      Tax {status || "—"}
    </span>
  );
}

function IdentityStrip({ row, a, g }) {
  const cur = g.current || {}; const sub = g.proposed || {};
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] font-mono text-zinc-400 mb-3" data-testid="approvals-gst-identity">
      <span className="text-white">{row.doc_no} · {a.amendment_no}</span>
      <span data-testid="approvals-gst-revisions">Current Rev {cur.po_revision ?? "—"} (Snapshot #{cur.seq ?? "—"}) → Proposed Rev {sub.proposed_revision ?? "—"}</span>
      <span>Submitted by <span className="text-zinc-200">{a.submitted_by || "—"}</span> · {fmtTs(sub.submitted_at || a.submitted_at)}</span>
      <span>Rate basis {sub.rate_basis_date || "—"}</span>
      <span title={`${cur.fingerprint || ""} → ${sub.fingerprint || ""}`}>fp {shortFp(cur.fingerprint)} → {shortFp(sub.fingerprint)} · input {shortFp(sub.input_fingerprint)}</span>
    </div>
  );
}

function TotalsTable({ g }) {
  const ct = g.current?.totals || {}; const st = g.proposed?.totals || {};
  return (
    <table className="w-full text-xs" data-testid="approvals-gst-totals">
      <thead className="text-[10px] uppercase tracking-widest text-zinc-500">
        <tr>
          <th className="px-2 py-1 text-left">GST Totals</th>
          <th className="px-2 py-1 text-right whitespace-nowrap">Current <StatusChip status={g.current?.tax_status} testid="approvals-gst-current-status" /></th>
          <th className="px-2 py-1 text-right whitespace-nowrap">Submitted <StatusChip status={g.proposed?.tax_status} testid="approvals-gst-submitted-status" /></th>
          <th className="px-2 py-1 text-right">Difference</th>
        </tr>
      </thead>
      <tbody>
        {TOTAL_ROWS.map(([k, label]) => {
          const d = totalDiff(ct[k], st[k]);
          const grand = k === "grand_total" || k === "goods_taxable_before_charges";
          return (
            <tr key={k} className={`border-t border-[var(--border)] ${grand ? "font-semibold" : ""}`} data-testid={`approvals-gst-row-${k}`}>
              <td className="px-2 py-1 text-zinc-300">{label}</td>
              <td className="px-2 py-1 text-right font-mono tabular-nums text-zinc-400" data-testid={`approvals-gst-cur-${k}`}>{money(ct[k])}</td>
              <td className={`px-2 py-1 text-right font-mono tabular-nums ${d ? chg : "text-zinc-300"}`} data-testid={`approvals-gst-sub-${k}`}>{money(st[k])}</td>
              <td className={`px-2 py-1 text-right font-mono tabular-nums ${d == null ? "text-zinc-600" : d > 0 ? "text-amber-300" : d < 0 ? "text-emerald-300" : "text-zinc-600"}`} data-testid={`approvals-gst-diff-${k}`}>
                {d == null ? "—" : d < 0 ? `-${money(-d)}` : `${d > 0 ? "+" : ""}${money(d)}`}
              </td>
            </tr>
          );
        })}
        <tr className="border-t border-[var(--border)]">
          <td className="px-2 py-1 text-zinc-300">Classification</td>
          <td className="px-2 py-1 text-right font-mono text-[10px] text-zinc-400" data-testid="approvals-gst-cur-class">{taxType(g.current?.classification)}</td>
          <td className={`px-2 py-1 text-right font-mono text-[10px] ${same(g.current?.classification?.tax_type, g.proposed?.classification?.tax_type) ? "text-zinc-300" : chg}`} data-testid="approvals-gst-sub-class">{taxType(g.proposed?.classification)}</td>
          <td />
        </tr>
      </tbody>
    </table>
  );
}

const TERM_KEYS = ["place_of_supply_state_code", "company_is_receiving_location", "reverse_charge", "expected_date"];
const BOOL_KEYS = ["company_is_receiving_location", "reverse_charge"];
const termVal = (k, v) => (typeof v === "boolean" ? (v ? "Yes" : "No") : (v == null || v === "" ? "—" : String(v).slice(0, 10)));

function TermsTable({ terms }) {
  const cur = terms?.current || {}; const prop = terms?.proposed;
  if (!prop) return <div className="text-[11px] text-zinc-500">Proposed terms unavailable in queue data.</div>;
  const cc = cur.charges || []; const pc = prop.charges || [];
  return (
    <table className="w-full text-xs" data-testid="approvals-gst-terms">
      <thead className="text-[10px] uppercase tracking-widest text-zinc-500">
        <tr><th className="px-2 py-1 text-left">GST &amp; Terms</th><th className="px-2 py-1 text-left">Current</th><th className="px-2 py-1 text-left">Proposed</th></tr>
      </thead>
      <tbody>
        {TERM_KEYS.map((k) => {
          const norm = (v) => (BOOL_KEYS.includes(k) ? v === true : k === "expected_date" ? (v || "").slice(0, 10) : (v || ""));
          const c = norm(cur[k]); const p = norm(prop[k]);
          const changed = c !== p;
          return (
            <tr key={k} className="border-t border-[var(--border)]" data-testid={`approvals-gst-term-${k}`}>
              <td className="px-2 py-1 text-zinc-300">{fieldLabel(k)}</td>
              <td className="px-2 py-1 font-mono text-zinc-400">{termVal(k, c)}</td>
              <td className={`px-2 py-1 font-mono ${changed ? chg : "text-zinc-300"}`} data-testid={`approvals-gst-term-${k}-prop`}>{termVal(k, p)}</td>
            </tr>
          );
        })}
        <tr className="border-t border-[var(--border)] align-top" data-testid="approvals-gst-term-charges">
          <td className="px-2 py-1 text-zinc-300">Charges</td>
          <td className="px-2 py-1 font-mono text-[10px] text-zinc-400">{cc.length ? cc.map((c, i) => <div key={i}>{chargeText(c)}</div>) : "None"}</td>
          <td className={`px-2 py-1 font-mono text-[10px] ${same(cc, pc) ? "text-zinc-300" : chg}`} data-testid="approvals-gst-term-charges-prop">{pc.length ? pc.map((c, i) => <div key={i}>{chargeText(c)}</div>) : "None"}</td>
        </tr>
      </tbody>
    </table>
  );
}

const LINE_FIELDS = [
  ["hsn_code", "HSN", (l, s) => l[`${s}_hsn_code`] || "—"],
  ["tax_pct", "GST rate", (l, s) => rateLabel(l[`${s}_tax_pct`], l[`${s}_gst_rate_source`])],
  ["discount_pct", "Disc %", (l, s) => (l[`${s}_discount_pct`] == null ? "—" : `${l[`${s}_discount_pct`]}%`)],
  ["discount_amt", "Disc ₹", (l, s) => money(l[`${s}_discount_amt`])],
  ["expected_date", "Delivery", (l, s) => (l[`${s}_expected_date`] || "").slice(0, 10) || "—"],
];

function LineGstTable({ lines }) {
  return (
    <table className="w-full text-xs" data-testid="approvals-gst-lines">
      <thead className="text-[10px] uppercase tracking-widest text-zinc-500">
        <tr>
          <th className="px-2 py-1 text-left">Line</th>
          {LINE_FIELDS.map(([k, label]) => <th key={k} className="px-2 py-1 text-left">{label} (Cur → Prop)</th>)}
        </tr>
      </thead>
      <tbody>
        {(lines || []).map((l) => (
          <tr key={l.line_no} className="border-t border-[var(--border)]" data-testid={`approvals-gst-line-${l.line_no}`}>
            <td className="px-2 py-1 font-mono text-[var(--accent)]">{String(l.line_no).padStart(3, "0")} {l.rm_code}</td>
            {LINE_FIELDS.map(([k, , fmt]) => {
              const c = fmt(l, "current"); const p = fmt(l, "proposed");
              return (
                <td key={k} className="px-2 py-1 font-mono text-[11px]" data-testid={`approvals-gst-line-${l.line_no}-${k}`}>
                  <span className="text-zinc-500">{c}</span> → <span className={c !== p ? chg : "text-zinc-300"}>{p}</span>
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Findings({ items }) {
  if (!items?.length) return null;
  return (
    <ul className="mt-2 space-y-0.5" data-testid="approvals-gst-findings">
      {items.map((f, i) => (
        <li key={i} className={`text-[11px] font-mono ${f.severity === "BLOCKING" ? "text-rose-300" : "text-amber-300"}`}>
          Current snapshot · {f.line_no ? `L${f.line_no} · ` : ""}{f.message || f.code} <span className="text-zinc-600">[{f.code}]</span>
          {f.code === SUPPLIER_CLASS_FINDING && <div data-testid="approvals-gst-supplier-class-guidance" className="text-sky-300 pl-4">{SUPPLIER_CLASS_GUIDANCE}</div>}
        </li>
      ))}
    </ul>
  );
}

export function PoAmendGstComparison({ row }) {
  const a = row?.amendment;
  const g = a?.gst_comparison;
  if (!g) return null;
  return (
    <div data-testid="approvals-gst-comparison" className="border border-sky-500/40 bg-sky-500/5 rounded-sm p-3 mb-4 space-y-3">
      <div className="overline text-sky-300">GST Comparison · Current Effective vs Submitted (stored, read-only)</div>
      <IdentityStrip row={row} a={a} g={g} />
      {!g.proposed && <div data-testid="approvals-gst-no-submitted" className="text-xs text-amber-300">No submitted GST calculation is stored for this amendment — the server will validate on Authorise.</div>}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="min-w-0 overflow-x-auto"><TotalsTable g={g} /></div>
        <div className="min-w-0 overflow-x-auto"><TermsTable terms={g.terms} /></div>
      </div>
      <div className="overflow-x-auto"><LineGstTable lines={a.proposed_lines} /></div>
      <Findings items={g.current?.findings} />
      <div className="text-[10px] font-mono text-zinc-500">Per-line GST amounts are not shown — the submitted calculation stores totals only. Cyan = changed.</div>
    </div>
  );
}

export function LegacyAmendChip() {
  return (
    <span data-testid="approvals-legacy-chip" className="inline-flex items-center px-2 py-0.5 rounded-sm border border-zinc-500/50 bg-zinc-500/10 text-zinc-300 text-[10px] font-mono uppercase tracking-widest">
      LEGACY · No GST Control
    </span>
  );
}
